'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useChart } from '@/contexts/chart-context';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
    type DetailResponse,
    type PriceHistoryItem,
    type Response,
    type StoreBookmarkStationResponse,
} from '@/lib/api/data-contracts';
import { FuelStations } from '@/lib/api/FuelStationsRoute';
import { Stores } from '@/lib/api/StoresRoute';
import { FUEL_COLORS, FUEL_LABELS, type FuelType } from '@/constants/fuel-colors';
import { useCompanyStore } from '@/stores/company-store';
import HighchartsReact from 'highcharts-react-official';
import { ArrowDown, ArrowUp, ArrowUpDown, Bookmark, Edit2, Loader2, Plus, Search, Settings, Store, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';




// BookmarkStation에서 유종 가격을 안전하게 꺼내는 헬퍼
const getBookmarkFuelPrice = (bookmark: StoreBookmarkStationResponse, fuelType: FuelType): number => {
    return ((bookmark as Record<string, unknown>)[fuelType] as number) ?? 0;
};

// 유종별 비교 차트의 최소 y축 표시 폭(원)
const MIN_Y_RANGE = 100;

// 주유소별 라인 색상 팔레트 (동일 유종 내에서 주유소 구분용)
const STATION_LINE_COLORS = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // emerald
    '#f59e0b', // amber
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#14b8a6', // teal
    '#6366f1', // indigo
];

// 주유소별 유가 데이터
interface StationPriceData {
    bookmark: StoreBookmarkStationResponse;
    priceHistory: PriceHistoryItem[];
}

// 유종별 비교 차트 컴포넌트
interface FuelTypeChartProps {
    fuelType: FuelType;
    stationDataList: StationPriceData[];
}

function FuelTypeComparisonChart({ fuelType, stationDataList }: FuelTypeChartProps) {
    const { Highcharts, isHighchartsLoaded } = useChart();
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const fuelColor = FUEL_COLORS[fuelType];
    const fuelLabel = FUEL_LABELS[fuelType];

    // 해당 유종 데이터가 있는 주유소만 필터링
    const relevantStations = stationDataList.filter(sd => {
        const hasFuelPrice = (getBookmarkFuelPrice(sd.bookmark, fuelType)) > 0;
        const hasHistoryData = sd.priceHistory.some(item => (item[fuelType] ?? 0) > 0);
        return hasFuelPrice || hasHistoryData;
    });

    if (relevantStations.length === 0 || !isHighchartsLoaded) return null;

    // 모든 주유소의 날짜를 합치고, 날짜별로 각 주유소의 가격을 매핑
    const allDatesMap = new Map<string, Record<string, number | null>>();

    relevantStations.forEach((sd, stationIdx) => {
        const groupedByDate = sd.priceHistory.reduce((acc, item) => {
            if (!item.recordedAt) return acc;
            const date = new Date(item.recordedAt);
            const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

            if (!acc[dateKey] || new Date(item.recordedAt).getTime() > new Date(acc[dateKey].recordedAt!).getTime()) {
                acc[dateKey] = item;
            }
            return acc;
        }, {} as Record<string, PriceHistoryItem>);

        Object.entries(groupedByDate).forEach(([dateKey, item]) => {
            if (!allDatesMap.has(dateKey)) {
                allDatesMap.set(dateKey, {});
            }
            const dateRecord = allDatesMap.get(dateKey)!;
            const price = item[fuelType];
            dateRecord[`station_${stationIdx}`] = price && price > 0 ? price : null;
        });
    });

    const sortedDates = Array.from(allDatesMap.entries()).sort(([a], [b]) => a.localeCompare(b));
    const categories = sortedDates.map(([dateKey]) => {
        const [, month, day] = dateKey.split('-').map(Number);
        return `${month}/${day}`;
    });

    if (categories.length === 0) return null;

    // 값 변동이 작은 기간에도 최소 100원 폭을 확보한다 (평평한 구간이 한 줄로 뭉개지는 것 방지)
    const prices = sortedDates
        .flatMap(([, byStation]) => Object.values(byStation))
        .filter((price): price is number => price !== null);
    const dataMin = prices.length > 0 ? Math.min(...prices) : 0;
    const dataMax = prices.length > 0 ? Math.max(...prices) : 0;
    const pad = prices.length > 0 ? Math.max(0, (MIN_Y_RANGE - (dataMax - dataMin)) / 2) : 0;

    const chartOptions = {
        chart: {
            type: 'spline',
            height: 200,
            backgroundColor: '#2d353c',
        },
        title: {
            text: fuelLabel,
            align: 'center' as const,
            style: { color: fuelColor.primary, fontSize: '14px', fontWeight: 'bold' },
        },
        xAxis: {
            categories,
            labels: { style: { color: '#999', fontSize: '11px' } },
            lineColor: '#444',
            tickColor: '#444',
        },
        yAxis: {
            title: { text: '' },
            ...(pad > 0 && { min: dataMin - pad, max: dataMax + pad }),
            labels: {
                format: '{value:,.0f}',
                style: { color: '#999', fontSize: '10px' },
            },
            gridLineColor: '#444',
            startOnTick: false,
            endOnTick: false,
            maxPadding: 0.02,
            minPadding: 0.02,
        },
        tooltip: {
            shared: true,
            headerFormat: '<b>{point.key}</b> — ' + fuelLabel + '<br/>',
            pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:,.0f}원</b><br/>',
        },
        legend: {
            layout: 'horizontal' as const,
            align: 'center' as const,
            verticalAlign: 'bottom' as const,
            itemStyle: { color: '#999', fontSize: '11px' },
        },
        // 30일치 점이 겹쳐 선이 안 보이므로 마커는 끄고 선만 그린다
        plotOptions: { spline: { marker: { enabled: false } } },
        series: relevantStations.map((sd, idx) => ({
            name: sd.bookmark.alias || sd.bookmark.stationName || `주유소${idx + 1}`,
            color: STATION_LINE_COLORS[idx % STATION_LINE_COLORS.length],
            data: sortedDates.map(([, prices]) => prices[`station_${idx}`] ?? null),
            connectNulls: true,
        })),
        exporting: { enabled: false },
        credits: { enabled: false },
    };

    return (
        <div className="overflow-hidden border" style={{ borderLeft: `4px solid ${fuelColor.primary}` }}>
            <div>
                <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />
            </div>
        </div>
    );
}

// 관심 주유소 관리 모달 컴포넌트
interface BookmarkManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStoreCd: string;
    onStoreCdChange: (storeCd: string) => void;
    storeOptions: { value: string; label: string }[];
    bookmarks: StoreBookmarkStationResponse[];
    onBookmarksChange: () => void;
}

function BookmarkManagementModal({
    isOpen,
    onClose,
    selectedStoreCd,
    onStoreCdChange,
    storeOptions,
    bookmarks,
    onBookmarksChange,
}: BookmarkManagementModalProps) {
    const { toast } = useToast();

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Response[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [editingBookmark, setEditingBookmark] = useState<StoreBookmarkStationResponse | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editAlias, setEditAlias] = useState('');
    const [editMemo, setEditMemo] = useState('');

    const storeCd = selectedStoreCd || null;

    // 주유소 검색
    const handleSearch = async () => {
        if (!searchQuery.trim() || searchQuery.length < 2) {
            toast({
                title: '알림',
                description: '검색어는 2자 이상 입력해주세요.',
            });
            return;
        }

        setIsSearching(true);
        try {
            const response = await apiClient
                .get('fuel-stations/search', {
                    searchParams: { query: searchQuery },
                })
                .json<FuelStations.SearchByText.ResponseBody>();

            setSearchResults(response);
        } catch (error) {
            console.error('Failed to search stations:', error);
            toast({
                title: '오류',
                description: '주유소 검색에 실패했습니다.',
                variant: 'destructive',
            });
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // 북마크 추가
    const handleAddBookmark = async (station: Response) => {
        if (!storeCd) {
            toast({
                title: '오류',
                description: '매장을 선택해주세요.',
                variant: 'destructive',
            });
            return;
        }

        if (bookmarks.some(b => b.fuelStationId === station.id)) {
            toast({
                title: '알림',
                description: '이미 북마크된 주유소입니다.',
            });
            return;
        }

        try {
            const requestBody: Stores.CreateStoreBookmarkStation.RequestBody = {
                fuelStationId: station.id || '',
            };

            await apiClient
                .post(`stores/${storeCd}/bookmark-stations`, {
                    json: requestBody,
                })
                .json<Stores.CreateStoreBookmarkStation.ResponseBody>();

            toast({
                title: '성공',
                description: '북마크가 추가되었습니다.',
            });

            onBookmarksChange();
        } catch (error) {
            console.error('Failed to add bookmark:', error);
            toast({
                title: '오류',
                description: '북마크 추가에 실패했습니다.',
                variant: 'destructive',
            });
        }
    };

    // 북마크 삭제
    const handleDeleteBookmark = async (bookmarkId: number) => {
        if (!storeCd) return;

        try {
            await apiClient.delete(`stores/${storeCd}/bookmark-stations/${bookmarkId}`).json();
            toast({ title: '성공', description: '북마크가 삭제되었습니다.' });
            onBookmarksChange();
        } catch (error) {
            console.error('Failed to delete bookmark:', error);
            toast({ title: '오류', description: '북마크 삭제에 실패했습니다.', variant: 'destructive' });
        }
    };

    // 북마크 수정 다이얼로그 열기
    const handleOpenEditDialog = (bookmark: StoreBookmarkStationResponse) => {
        setEditingBookmark(bookmark);
        setEditAlias(bookmark.alias || '');
        setEditMemo(bookmark.memo || '');
        setIsEditDialogOpen(true);
    };

    // 북마크 수정
    const handleUpdateBookmark = async () => {
        if (!storeCd || !editingBookmark) return;

        try {
            const requestBody: Stores.UpdateStoreBookmarkStation.RequestBody = {
                alias: editAlias.trim() || undefined,
                memo: editMemo.trim() || undefined,
            };

            await apiClient
                .put(`stores/${storeCd}/bookmark-stations/${editingBookmark.id}`, {
                    json: requestBody,
                })
                .json<Stores.UpdateStoreBookmarkStation.ResponseBody>();

            toast({ title: '성공', description: '북마크가 수정되었습니다.' });
            setIsEditDialogOpen(false);
            setEditingBookmark(null);
            onBookmarksChange();
        } catch (error) {
            console.error('Failed to update bookmark:', error);
            toast({ title: '오류', description: '북마크 수정에 실패했습니다.', variant: 'destructive' });
        }
    };

    const handleClose = () => {
        setSearchQuery('');
        setSearchResults([]);
        onClose();
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className='max-w-4xl max-h-[90vh] overflow-hidden flex flex-col'>
                    <DialogHeader>
                        <DialogTitle className='flex items-center gap-2'>
                            <Settings className='h-5 w-5' />
                            관심 주유소 관리
                        </DialogTitle>
                        <DialogDescription>
                            주유소를 검색하여 북마크하고 관리할 수 있습니다.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='flex-1 overflow-y-auto space-y-4 py-4 px-1'>
                        {/* 매장 선택 */}
                        <div className='space-y-2'>
                            <Label className='flex items-center gap-2'>
                                <Store className='h-4 w-4' />
                                매장 선택
                            </Label>
                            <Select value={selectedStoreCd} onValueChange={onStoreCdChange}>
                                <SelectTrigger className='w-full'>
                                    <SelectValue placeholder='매장을 선택하세요' />
                                </SelectTrigger>
                                <SelectContent>
                                    {storeOptions.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {storeOptions.length === 0 && (
                                <p className='text-sm text-muted-foreground'>사용 가능한 매장이 없습니다.</p>
                            )}
                        </div>

                        {!storeCd ? (
                            <div className='text-center py-8 border rounded-lg'>
                                <Store className='h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50' />
                                <p className='text-muted-foreground'>
                                    위에서 매장을 선택하면 관심 주유소를 관리할 수 있습니다.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* 주유소 검색 섹션 */}
                                <div className='space-y-3 border rounded-lg p-4 bg-slate-50'>
                                    <h3 className='font-medium'>주유소 검색</h3>
                                    <div className='flex gap-2'>
                                        <Input
                                            placeholder='주유소 이름 또는 주소를 입력하세요 (최소 2자)'
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                                            className='flex-1'
                                        />
                                        <Button onClick={handleSearch} disabled={isSearching || searchQuery.length < 2}>
                                            {isSearching ? (
                                                <><Loader2 className='mr-2 h-4 w-4 animate-spin' />검색 중...</>
                                            ) : (
                                                <><Search className='mr-2 h-4 w-4' />검색</>
                                            )}
                                        </Button>
                                    </div>

                                    {searchResults.length > 0 && (
                                        <div className='space-y-2'>
                                            <h4 className='text-sm font-medium'>검색 결과 ({searchResults.length}개)</h4>
                                            <div className='space-y-2 max-h-48 overflow-y-auto'>
                                                {searchResults.map(station => {
                                                    const isBookmarked = bookmarks.some(b => b.fuelStationId === station.id);
                                                    return (
                                                        <div key={station.id} className='flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-accent'>
                                                            <div className='flex-1'>
                                                                <div className='font-medium'>{station.name}</div>
                                                                <div className='text-sm text-muted-foreground'>{station.roadAddress || station.jibunAddress}</div>
                                                            </div>
                                                            <Button size='sm' onClick={() => handleAddBookmark(station)} disabled={isBookmarked} variant={isBookmarked ? 'outline' : 'default'}>
                                                                {isBookmarked ? (<><Bookmark className='mr-2 h-4 w-4 fill-current' />북마크됨</>) : (<><Plus className='mr-2 h-4 w-4' />추가</>)}
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 북마크 목록 섹션 */}
                                <div className='space-y-3 border rounded-lg p-4 bg-blue-50'>
                                    <h3 className='font-medium'>북마크된 주유소 ({bookmarks.length}개)</h3>
                                    {bookmarks.length === 0 ? (
                                        <div className='text-center py-6 text-muted-foreground'>
                                            <Bookmark className='h-10 w-10 mx-auto mb-3 opacity-50' />
                                            <p>북마크된 주유소가 없습니다.</p>
                                            <p className='text-sm mt-1'>위 검색 기능을 사용하여 주유소를 북마크하세요.</p>
                                        </div>
                                    ) : (
                                        <div className='space-y-2 max-h-64 overflow-y-auto'>
                                            {bookmarks.map(bookmark => (
                                                <div key={bookmark.id} className='flex items-start justify-between p-3 border rounded-lg bg-white hover:bg-accent'>
                                                    <div className='flex-1'>
                                                        <div className='font-medium'>{bookmark.alias || bookmark.stationName}</div>
                                                        {bookmark.alias && <div className='text-sm text-muted-foreground'>{bookmark.stationName}</div>}
                                                        <div className='text-sm text-muted-foreground mt-1'>{bookmark.roadAddress}</div>
                                                        {bookmark.memo && <div className='text-sm text-muted-foreground mt-1 italic'>{bookmark.memo}</div>}
                                                        <div className='flex gap-4 mt-2 text-xs'>
                                                            {(bookmark.gas ?? 0) > 0 && <div><span className='text-muted-foreground'>휘발유: </span><span className='font-medium'>{bookmark.gas!.toLocaleString()}원</span></div>}
                                                            {(bookmark.diesel ?? 0) > 0 && <div><span className='text-muted-foreground'>경유: </span><span className='font-medium'>{bookmark.diesel!.toLocaleString()}원</span></div>}
                                                        </div>
                                                    </div>
                                                    <div className='flex gap-2 ml-4'>
                                                        <Button size='sm' variant='outline' onClick={() => handleOpenEditDialog(bookmark)}><Edit2 className='h-4 w-4' /></Button>
                                                        <Button size='sm' variant='destructive' onClick={() => { if (confirm('정말 삭제하시겠습니까?')) handleDeleteBookmark(bookmark.id!); }}><Trash2 className='h-4 w-4' /></Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant='outline' onClick={handleClose}>닫기</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* 수정 다이얼로그 */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>북마크 수정</DialogTitle>
                        <DialogDescription>별칭과 메모를 수정할 수 있습니다.</DialogDescription>
                    </DialogHeader>
                    <div className='space-y-4 py-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='alias'>별칭 (선택사항)</Label>
                            <Input id='alias' placeholder='예: 단골 주유소' value={editAlias} onChange={e => setEditAlias(e.target.value)} maxLength={50} />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='memo'>메모 (선택사항)</Label>
                            <Textarea id='memo' placeholder='예: 셀프 주유소, 세차장 있음' value={editMemo} onChange={e => setEditMemo(e.target.value)} maxLength={200} rows={3} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setIsEditDialogOpen(false)}>취소</Button>
                        <Button onClick={handleUpdateBookmark}>저장</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// 메인 컴포넌트
export function BookmarkedStations() {
    const { selectedCompany } = useCompanyStore();
    const { getOrganizationsByCompany, getStoresByOrganization, isLoading: isLoadingOrg } = useOrganization();
    const { toast } = useToast();

    const [selectedStoreCd, setSelectedStoreCd] = useState<string>('');
    const [bookmarks, setBookmarks] = useState<StoreBookmarkStationResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [stationDataList, setStationDataList] = useState<StationPriceData[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isManagementModalOpen, setIsManagementModalOpen] = useState(false);
    const [sortFuel, setSortFuel] = useState<{ key: FuelType; asc: boolean } | null>(null);

    // 선택한 회사의 매장 목록 (조직 데이터에서 유도)
    const storeOptions = useMemo(
        () =>
            selectedCompany
                ? getOrganizationsByCompany(selectedCompany).flatMap(org =>
                    getStoresByOrganization(org.orgCd!).map(store => ({
                        value: store.orgCd!,
                        label: store.orgName!,
                    }))
                )
                : [],
        [selectedCompany, getOrganizationsByCompany, getStoresByOrganization]
    );

    // 선택 매장이 목록에 없으면(회사 전환·목록 로드 전) 첫 매장을 사용
    const storeCd =
        (storeOptions.some(o => o.value === selectedStoreCd) ? selectedStoreCd : storeOptions[0]?.value) ?? null;

    // 북마크 목록 조회
    const loadBookmarks = useCallback(async () => {
        if (!storeCd) {
            setBookmarks([]);
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiClient
                .get(`stores/${storeCd}/bookmark-stations`)
                .json<Stores.GetStoreBookmarkStations.ResponseBody>();

            if (response.result) {
                setBookmarks(response.result);
            }
        } catch (error) {
            console.error('Failed to load bookmarks:', error);
            toast({ title: '오류', description: '북마크 목록을 불러오는데 실패했습니다.', variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    }, [storeCd, toast]);

    useEffect(() => {
        loadBookmarks();
    }, [loadBookmarks]);

    // 북마크 변경 시 전체 주유소 유가 이력 로드
    useEffect(() => {
        if (bookmarks.length === 0) {
            setStationDataList([]);
            return;
        }

        const loadAllPriceHistories = async () => {
            setIsLoadingHistory(true);
            try {
                const results = await Promise.all(
                    bookmarks.map(async (bookmark) => {
                        if (!bookmark.fuelStationId) {
                            return { bookmark, priceHistory: [] };
                        }
                        try {
                            const response = await apiClient
                                .get(`fuel-stations/search/${bookmark.fuelStationId}`, {
                                    searchParams: { days: '30' },
                                })
                                .json<DetailResponse>();
                            return { bookmark, priceHistory: response.priceHistory || [] };
                        } catch {
                            return { bookmark, priceHistory: [] };
                        }
                    })
                );
                setStationDataList(results);
            } finally {
                setIsLoadingHistory(false);
            }
        };

        loadAllPriceHistories();
    }, [bookmarks]);

    // 유종 헤더 클릭: 저가순 → 고가순 → 기본순
    const toggleSort = (key: FuelType) => {
        setSortFuel(prev => (prev?.key !== key ? { key, asc: true } : prev.asc ? { key, asc: false } : null));
    };

    // 선택한 유종 가격순 정렬 (가격 없는 주유소는 항상 아래)
    const sortedBookmarks = useMemo(() => {
        if (!sortFuel) return bookmarks;
        const { key, asc } = sortFuel;
        return [...bookmarks].sort((a, b) => {
            const pa = getBookmarkFuelPrice(a, key);
            const pb = getBookmarkFuelPrice(b, key);
            if (pa === 0 || pb === 0) return pa === pb ? 0 : pa === 0 ? 1 : -1;
            return asc ? pa - pb : pb - pa;
        });
    }, [bookmarks, sortFuel]);

    // 데이터가 있는 유종 목록 (표시 순서)
    const activeFuelTypes = useMemo(() => {
        const fuelOrder: FuelType[] = ['premiumGas', 'gas', 'diesel', 'lpg'];
        return fuelOrder.filter(fuelType =>
            stationDataList.some(sd => {
                const hasFuelPrice = (getBookmarkFuelPrice(sd.bookmark, fuelType)) > 0;
                const hasHistoryData = sd.priceHistory.some(item => (item[fuelType] ?? 0) > 0);
                return hasFuelPrice || hasHistoryData;
            })
        );
    }, [stationDataList]);

    if (isLoadingOrg) {
        return (
            <Card>
                <CardContent className='p-4'>
                    <div className='flex items-center justify-center py-8'>
                        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                        <span className='ml-2 text-muted-foreground'>로딩 중...</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 p-1 pl-2'>
                    <CardTitle>관심 주유소</CardTitle>
                    <Button variant='ghost' size='sm' className='h-5 text-xs' onClick={() => setIsManagementModalOpen(true)}>
                        <Settings className='mr-1' />
                        관리
                    </Button>
                </CardHeader>
                <CardContent className='space-y-3'>
                    <div>
                        <Select value={storeCd ?? ''} onValueChange={setSelectedStoreCd}>
                            <SelectTrigger className='w-full'>
                                <SelectValue placeholder='매장을 선택하세요' />
                            </SelectTrigger>
                            <SelectContent>
                                {storeOptions.map(option => (
                                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {(isLoading || isLoadingHistory) ? (
                        <div className='flex items-center justify-center py-2'>
                            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                            <span className='ml-2 text-muted-foreground'>로딩 중...</span>
                        </div>
                    ) : bookmarks.length === 0 ? (
                        <div className='text-center py-2'>
                            <div className='flex items-center justify-center'>
                                <Bookmark className='opacity-50' />
                                <p className='text-muted-foreground'>북마크된 주유소가 없습니다.</p>
                            </div>
                        </div>
                    ) : null}

                    {/* 주유소별 최근 가격 테이블 */}
                    {!isLoading && !isLoadingHistory && stationDataList.length > 0 && activeFuelTypes.length > 0 && (
                        <div className='overflow-x-auto rounded-lg border'>
                            <table className='w-full text-sm whitespace-nowrap'>
                                <thead>
                                    <tr className='border-b bg-muted/50'>
                                        <th className='text-left py-2 px-3 font-medium'>주유소</th>
                                        {activeFuelTypes.map(ft => (
                                            <th key={ft} className='text-right py-2 px-3 font-medium'>
                                                <button
                                                    type='button'
                                                    onClick={() => toggleSort(ft)}
                                                    title={`${FUEL_LABELS[ft]} 가격순 정렬`}
                                                    className='flex items-center justify-end gap-1.5 w-full hover:text-foreground'
                                                >
                                                    <div className='w-2 h-2 rounded-full' style={{ backgroundColor: FUEL_COLORS[ft].primary }} />
                                                    {FUEL_LABELS[ft]}
                                                    {sortFuel?.key !== ft ? (
                                                        <ArrowUpDown className='h-3 w-3 opacity-30' />
                                                    ) : sortFuel.asc ? (
                                                        <ArrowUp className='h-3 w-3' />
                                                    ) : (
                                                        <ArrowDown className='h-3 w-3' />
                                                    )}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedBookmarks.map(bookmark => (
                                        <tr key={bookmark.id} className='border-b last:border-b-0 hover:bg-muted/30'>
                                            <td className='py-2 px-3 font-medium truncate max-w-[120px]'>
                                                {bookmark.alias || bookmark.stationName}
                                            </td>
                                            {activeFuelTypes.map(ft => {
                                                const price = getBookmarkFuelPrice(bookmark, ft);
                                                return (
                                                    <td key={ft} className='text-right py-2 px-3 tabular-nums'>
                                                        {price > 0 ? `${price.toLocaleString()}원` : <span className='text-muted-foreground'>-</span>}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 유종별 비교 차트 */}
                    {!isLoading && !isLoadingHistory && stationDataList.length > 0 &&
                        activeFuelTypes.map(fuelType => (
                            <FuelTypeComparisonChart
                                key={fuelType}
                                fuelType={fuelType}
                                stationDataList={stationDataList}
                            />
                        ))
                    }
                </CardContent>
            </Card>

            <BookmarkManagementModal
                isOpen={isManagementModalOpen}
                onClose={() => setIsManagementModalOpen(false)}
                selectedStoreCd={storeCd ?? ''}
                onStoreCdChange={setSelectedStoreCd}
                storeOptions={storeOptions}
                bookmarks={bookmarks}
                onBookmarksChange={loadBookmarks}
            />
        </>
    );
}
